import { mailIcon } from "#webapp/src/assets/img/icons";
import { eventSurpassesMaxAttendeesForSuggestedSlots } from "#webapp/src/components/event/Event";
import { updateOrgInvite } from "#webapp/src/mutations";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer.js";
import { EFlex, getFlexFromEvent } from "#webapp/src/util/event-tag.util";
import { niceName } from "#webapp/src/util/event.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { EScheduleRange, getMaxMinSlotForRange } from "#webapp/src/util/scheduler.util";
import { useQuery } from "@apollo/client";
import { getValue, isValue } from "@clockwise/client-commons/src/util/errorable.util";
import { isRoom } from "@clockwise/client-commons/src/util/room";
import { Button, Link } from "@clockwise/design-system";
import { KeyboardArrowDown, KeyboardArrowUp } from "@clockwise/design-system/icons";
import { Person, ResponseStatusEnum, UrnTypeEnum } from "@clockwise/schema";
import { webappDefaultWindowName } from "@clockwise/web-commons/src/constants/route.constants";
import { TrackingEvents, track } from "@clockwise/web-commons/src/util/analytics.util";
import { getUrlForFeature } from "@clockwise/web-commons/src/util/routes.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { Skeleton } from "@material-ui/lab";
import classNames from "classnames";
import { compact, find, isEmpty } from "lodash";
import { DateTime, Interval } from "luxon";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFeatureFlag } from "../../launch-darkly";
import { getCurrentEnvironment } from "../../state/relay-environment";
import { QuickRescheduleControler } from "../quick-reschedule/QuickRescheduleControler";
import AttendeesSlotPains from "./AttendeesSlotPains";
import AttendeesSlotPainsSummary from "./AttendeesSlotPainsSummary";
import EventAttendeesAvatars from "./EventAttendeesAvatars";
import {
  EventSchedulingDocument,
  EventSchedulingQuery,
} from "./__generated__/EventScheduling.generated";
import { SuggestedRescheduleSlotsForEventDocument } from "./__generated__/SuggestedRescheduleSlotsForEvent.generated";

type Props = {
  orgId: string;
  externalEventId: string;
  eventCalendarId: string;
  enableRescheduling: boolean;
  hidePains?: boolean;
};

export const eventIconClasses = "cw-h-4 cw-w-4 cw-mr-2 cw-flex cw-shrink-0 cw-mt-0.5";
export const baseClasses =
  "cw-flex cw-gap-2 cw-text-13 cw-leading-4 cw-text-neutral cw-items-start";
export const rowClasses = `${baseClasses} cw-flex-row`;

export type EventSchedulingQueryOrg = Extract<EventSchedulingQuery["org"], { __typename: "Org" }>;
type ForceFetchEventParentErrorable = EventSchedulingQueryOrg["forceFetchEventParentErrorable"];
type ComputeCurrentSlotImpactErrorable = EventSchedulingQueryOrg["computeCurrentSlotImpactErrorable"];
type CurrentScheduledSlotImpact = Extract<
  ComputeCurrentSlotImpactErrorable,
  { __typename: "CurrentScheduledSlotImpact" }
>;
export type EventSchedulingQueryEventParent = Extract<
  ForceFetchEventParentErrorable,
  { __typename: "EventParent" }
>;
export type EventSchedulingQueryEvent = EventSchedulingQueryEventParent["events"][number];
export type EventSchedulingQueryAttendee = EventSchedulingQueryEvent["attendees"][number];
export type EventSchedulingQueryPerson = Extract<
  EventSchedulingQueryEvent["attendeePeople"],
  { __typename: "PersonList" }
>["list"][number];

type EventSchedulingQueryCurrentSlot = Extract<
  CurrentScheduledSlotImpact["currentSlot"],
  { __typename: "SchedulingSlot" }
>;
type EventSchedulingQuerySchedulingAttendanceImpact = EventSchedulingQueryCurrentSlot["attendanceImpacts"][number];
export type EventSchedulingQueryImpact = EventSchedulingQuerySchedulingAttendanceImpact;

export interface PersonImpact {
  impacts?: EventSchedulingQueryImpact;
  calendarId: string;
}

export type PersonMap = {
  [email: string]: EventSchedulingQueryPerson;
};
export type AttendeeMap = Record<string, EventSchedulingQueryAttendee>;

export type EventSchedulingOrg = Extract<EventSchedulingQuery["org"], { __typename: "Org" }>;

const aiUrl = getUrlForFeature("ai");

export const getOrderedAttendeesAndPersonsMaps = ({
  org,
  event,
}: {
  org: EventSchedulingOrg;
  event: EventSchedulingQueryEvent;
}): {
  personImpacts: PersonImpact[];
  personMap: PersonMap;
  attendeeMap: AttendeeMap;
} => {
  let personImpacts: PersonImpact[];

  // on errors, we will generate the view via the event attendees
  const currentScheduledSlotImpact = getValue(org?.computeCurrentSlotImpactErrorable);
  const currentSlot = getValue(currentScheduledSlotImpact?.currentSlot);

  if (!org || !currentScheduledSlotImpact || !currentSlot) {
    const attendeesMap = event.attendees
      .map((attendee) => ({ calendarId: attendee.urnValue }))
      .filter(({ calendarId }) => !isRoom(calendarId));
    const calendarIdsMap = event.calendarIds.map((calendarId) => ({ calendarId }));

    // if list of attendees is empty eg in the case of holds, fallback to calendarIds
    personImpacts = event.attendees.length > 0 ? attendeesMap : calendarIdsMap;
  } else {
    const { uncrawledCalendars } = currentScheduledSlotImpact;

    const { attendanceImpacts } = currentSlot;

    personImpacts = attendanceImpacts
      .filter((ai) => {
        // filter out rooms
        return !isRoom(ai.calendarId);
      })
      .sort((a, b) => {
        // sort most focus time cost first
        const aScore = -a.focusTimeMinutesCost / 60;
        const bScore = -b.focusTimeMinutesCost / 60;
        return aScore - bScore;
      })
      .map((impacts) => ({ impacts, calendarId: impacts.calendarId }));

    personImpacts.push.apply(
      personImpacts,
      compact(uncrawledCalendars || [])
        .filter((calendarId) => {
          // filter out rooms
          return !isRoom(calendarId);
        })
        .map((calendarId) => ({ calendarId })),
    );
  }

  const personMap: { [email: string]: EventSchedulingQueryPerson } = {};
  const attendeeMap: Record<string, EventSchedulingQueryAttendee> = {};
  event.attendees.forEach((attendee) => (attendeeMap[attendee.urnValue] = attendee));

  let personList: EventSchedulingQueryPerson[] = [];
  const attendeePeople = getValue(event?.attendeePeople);
  if (attendeePeople) {
    personList = attendeePeople.list;
    personList.forEach((person) => {
      personMap[person.primaryCalendar] = person;
    });
  }

  personImpacts = personImpacts.sort((a, b) => {
    const aAttendee = attendeeMap[a.calendarId] || null;
    const bAttendee = attendeeMap[b.calendarId] || null;
    const aPerson = personMap[a.calendarId] || null;
    const bPerson = personMap[b.calendarId] || null;

    type Person = typeof aPerson;
    const alphabetizeByFirstNameLastName = (
      aPerson: Person | null,
      bPerson: Person | null,
    ): number => {
      // Returns a number value (a compareFn value) that can used to sort strings alphabetically
      return niceName(aPerson, a.calendarId, true).localeCompare(
        niceName(bPerson, b.calendarId, true),
      );
    };

    if (aAttendee && aAttendee.organizer) {
      return -1;
    } else if (bAttendee && bAttendee.organizer) {
      return 1;
    }

    // Then put those with conflicts
    const aPersonHasConflict = a.impacts?.hasConflict;
    const bPersonHasConflict = b.impacts?.hasConflict;
    if (aPersonHasConflict && !bPersonHasConflict) {
      return -1;
    } else if (bPersonHasConflict && !aPersonHasConflict) {
      return 1;
    } else if (aPersonHasConflict && bPersonHasConflict) {
      return alphabetizeByFirstNameLastName(aPerson, bPerson);
    }

    // Then put those with inconveniences
    const aPersonHasInconveniences = !isEmpty(a.impacts?.inconveniences);
    const bPersonHasInconveniences = !isEmpty(b.impacts?.inconveniences);
    if (aPersonHasInconveniences && !bPersonHasInconveniences) {
      return -1;
    } else if (bPersonHasInconveniences && !aPersonHasInconveniences) {
      return 1;
    } else if (aPersonHasInconveniences && bPersonHasInconveniences) {
      return alphabetizeByFirstNameLastName(aPerson, bPerson);
    }

    // The put those who have RSVP-ed
    const aPersonHasRSVPed = aAttendee?.responseStatus == ResponseStatusEnum.Accepted;
    const bPersonHasRSVPed = bAttendee?.responseStatus == ResponseStatusEnum.Accepted;
    if (aPersonHasRSVPed && !bPersonHasRSVPed) {
      return -1;
    } else if (bPersonHasRSVPed && !aPersonHasRSVPed) {
      return 1;
    }

    // If none of the above apply, then alphabetize
    return alphabetizeByFirstNameLastName(aPerson, bPerson);
  });

  return {
    personImpacts,
    personMap,
    attendeeMap,
  };
};

export const attendeesHaveConflict = (
  currentSlot:
    | {
        summary: {
          conflictingAttendees: string[];
        };
      }
    | null
    | undefined,
) => {
  const summary = currentSlot?.summary;
  const conflictingAttendees = summary?.conflictingAttendees;
  return !isEmpty(conflictingAttendees);
};

type ShowMoreShowLessProps = {
  isExpanded: boolean;
  handleClick: () => void;
};
const ShowMoreShowLess = ({ isExpanded, handleClick }: ShowMoreShowLessProps) => {
  return (
    <div className={classNames(rowClasses, "-cw-ml-3")}>
      <Button
        variant="text"
        size="small"
        startIcon={isExpanded ? KeyboardArrowUp : KeyboardArrowDown}
        onClick={handleClick}
        cw-id="scheduling-summary-toggle"
      >
        {isExpanded ? "Show less" : "Show more"}
      </Button>
    </div>
  );
};

export function EventScheduling({
  orgId,
  externalEventId,
  eventCalendarId,
  enableRescheduling: _enableRescheduling,
}: Props) {
  const [quickRescheduleEnabled] = useFeatureFlag("rollout-sidebar-quick-rescheduling");
  const enableRescheduling = _enableRescheduling && quickRescheduleEnabled;

  const { data, loading } = useQuery(EventSchedulingDocument, {
    variables: {
      orgId,
      externalEventId,
      calendarId: eventCalendarId,
    },
    fetchPolicy: "no-cache",
  });

  const [recentInvitees, setRecentInvitees] = useState<Person[]>([]);

  const org = getValue(data?.org, "Org");
  const computeCurrentSlotImpactErrorable = getValue(org?.computeCurrentSlotImpactErrorable);
  const currentSlot = computeCurrentSlotImpactErrorable?.currentSlot;
  const eventParent = getValue(org?.forceFetchEventParentErrorable);
  const events = eventParent?.events;
  const event = find(events, (event) => event?.eventKey?.externalEventId === externalEventId);
  let flexStatus = EFlex.Pin;
  if (event?.__typename == "Event") {
    flexStatus = getFlexFromEvent(event, true);
  }
  const attendees = event?.attendees;
  const calendarAttendee = find(
    attendees,
    (attendee) => attendee?.urnType == UrnTypeEnum.Email && attendee?.urnValue == eventCalendarId,
  );
  const responseStatusOnCalendar = calendarAttendee?.responseStatus;

  const timeZone = getRenderTimeZone();

  const [isExpanded, setIsExpanded] = useState(
    !!event?.attendees?.length && event?.attendees?.length <= 3,
  );

  const activeViewEvent = useSelector((state: IReduxState) => state.webExtension.activeViewEvent);
  const { lastQuickRescheduleMillis = 0 } = activeViewEvent;
  const shouldEmphasizeQuickReschedule = Date.now().valueOf() - lastQuickRescheduleMillis < 250;

  const specifiedMoment = event?.startTime?.dateOrDateTime
    ? moment(event.startTime.dateOrDateTime)
    : undefined;
  const { rangeMin: reschedulerRangeMin, rangeMax: reschedulerRangeMax } = getMaxMinSlotForRange(
    EScheduleRange.SevenBeforeAndAfter,
    specifiedMoment,
  );

  // Prevents multiple queries when there's no event data yet
  // And prevents a query that would error when event had more than max attendees
  const skipQuery = !event || eventSurpassesMaxAttendeesForSuggestedSlots(event);
  const { data: eventSuggestionsData } = useQuery(SuggestedRescheduleSlotsForEventDocument, {
    variables: {
      orgId,
      externalEventId,
      timeZone,
      reschedulerRangeMin,
      reschedulerRangeMax,
    },
    fetchPolicy: "no-cache",
    skip: skipQuery,
  });

  const reschedulingOrg = getValue(eventSuggestionsData?.node, "Org");
  const reschedulingResponse = getValue(reschedulingOrg?.reschedulingResponseErrorable);
  const slots = reschedulingResponse?.slots || [];
  const hasDeclined = responseStatusOnCalendar == ResponseStatusEnum.Declined;
  const hasConflict = attendeesHaveConflict(currentSlot);
  const hasConflictButHasDeclined = hasConflict && hasDeclined; // UI depends on this state
  // Note that has not declined includes several states: Accepted, NeedsAction, Tentative
  const hasConflictButHasNotDeclined = hasConflict && !hasDeclined;

  useEffect(() => {
    if (hasConflict) {
      // If we show conflict notifications for an event, track as displayed.
      track(TrackingEvents.EVENT.SCHEDULING.CONFLICT_NOTIFICATION_DISPLAYED, {
        hasDeclined,
        externalEventId,
        hasNoSuggestions: isEmpty(slots),
      });
    }
  }, [externalEventId, hasConflict]);

  const onShowMoreShowLessClick = () => {
    const nextIsExpanded = !isExpanded;
    track(TrackingEvents.EVENT.SCHEDULING.CONFLICT_NOTIFICATION_CLICKED, {
      isExpanded: nextIsExpanded,
      externalEventId,
      hasConflict,
    });
    setIsExpanded(nextIsExpanded);
  };

  if (loading) {
    return (
      <div className="cw-whitespace-pre">
        <div className="cw-heading-base cw-mb-2">Scheduling</div>
        <Skeleton height={95} variant="rect" className="cw-bg-neutral cw-w-full" />
      </div>
    );
  }

  const onInvite = (invitee: Person | null) => {
    if (!invitee || !invitee.primaryEmail) {
      return;
    }
    const calendarIds = [invitee.primaryEmail];
    org &&
      updateOrgInvite(
        getCurrentEnvironment(),
        { calendarIds, orgRelayId: org.id },
        () => {
          const newRecentInvitees: Person[] = [...recentInvitees];
          if (
            newRecentInvitees.every(
              (newRecentInvitee) => newRecentInvitee.primaryEmail !== invitee.primaryEmail,
            )
          ) {
            newRecentInvitees.push(invitee);
          }
          setRecentInvitees(newRecentInvitees);
        },
        () => null,
      );
  };

  const personList = getValue(event?.attendeePeople)?.list || [];

  let nonUserAttendeesShown: Person[] = [];
  if (org && event) {
    const { personImpacts, personMap } = getOrderedAttendeesAndPersonsMaps({ org, event });
    const orderedPersonsList = personImpacts.map(
      (personImpact) => personMap[personImpact.calendarId],
    );
    const pendingInvites = org?.pendingInvites?.edges;
    const nonUserAttendees = orderedPersonsList.filter(
      (person) =>
        person &&
        person.isUser === false &&
        person.isYou === false &&
        pendingInvites?.every((edge) => edge?.node?.targetCalendarId !== person.primaryEmail),
    );
    nonUserAttendeesShown =
      nonUserAttendees.length > 2 ? nonUserAttendees.splice(0, 2) : nonUserAttendees;
  }

  const maybeRenderInvites = () => {
    return (
      <div className="cw-whitespace-normal">
        {flexStatus !== EFlex.Pin &&
          nonUserAttendeesShown.map((nonUserAttendee) => {
            const nonUserAttendeeGivenName =
              nonUserAttendee && nonUserAttendee.profile && nonUserAttendee.profile?.givenName;
            // user invited on previous page load
            const personInvitePending = org?.pendingInvites?.edges?.find(
              (edge) => edge?.node?.targetCalendarId === nonUserAttendee.primaryEmail,
            );
            // user invited on this page load
            const personRecentlyInvited = recentInvitees.find(
              (recentInvitee) => recentInvitee.primaryEmail === nonUserAttendee.primaryEmail,
            );
            return (
              !personInvitePending &&
              nonUserAttendeeGivenName && (
                <div key={nonUserAttendee.id} className="cw-flex cw-items-start cw-pt-2">
                  <img src={mailIcon} />
                  <div className="cw-pl-2">
                    {personRecentlyInvited ? (
                      <div className="cw-text-positive">Invite Sent!</div>
                    ) : (
                      <div>
                        <Link onClick={() => onInvite(nonUserAttendee)}>
                          Invite {nonUserAttendeeGivenName}
                        </Link>{" "}
                        to Clockwise so we can find the best time for this meeting.
                      </div>
                    )}
                  </div>
                </div>
              )
            );
          })}
      </div>
    );
  };

  return (
    <div className="cw-whitespace-pre">
      <div className="cw-heading-base cw-mb-2">
        {enableRescheduling ? "Quick Reschedule" : "Scheduling"}
      </div>
      {enableRescheduling && (
        <div className="cw-pb-4">
          <QuickRescheduleControler
            calendarId={eventCalendarId}
            emphasis={shouldEmphasizeQuickReschedule}
            eventStartTime={event?.startTime?.dateOrDateTime || ""}
            eventTitle={event?.title || "this event"}
            externalEventId={externalEventId}
            onHover={(highlightStartISO) => {
              if (!highlightStartISO || !event || !event.startTime || !event.endTime) {
                PostMessageManager.sendShowEventOnCalendar(null, null, false);
                return;
              }

              const startDateTime = DateTime.fromMillis(event.startTime.millis);
              const endDateTime = DateTime.fromMillis(event.endTime.millis);
              const interval = Interval.fromDateTimes(startDateTime, endDateTime);
              let minutes = interval.length("minutes");
              if (minutes < 5) {
                // minimum 5 minutes so element is visible
                minutes = 5;
              }

              const highlightStartMills = DateTime.fromISO(highlightStartISO).toMillis();

              PostMessageManager.sendShowEventOnCalendar(highlightStartMills, minutes, true);
            }}
            onRedirect={() => window.open(aiUrl, webappDefaultWindowName)}
          />
        </div>
      )}
      {!hasConflict && (
        <>
          <div className="cw-bg-neutral cw-flex cw-flex-col cw-caption cw-p-4 cw-pb-2 cw-rounded cw-gap-3">
            {!isExpanded && (
              <div>
                <EventAttendeesAvatars org={org} event={event} />
                {maybeRenderInvites()}
              </div>
            )}

            {isExpanded && <AttendeesSlotPains org={org} event={event} onInvite={onInvite} />}
            {org && isValue(org.computeCurrentSlotImpactErrorable) && (
              <ShowMoreShowLess isExpanded={isExpanded} handleClick={onShowMoreShowLessClick} />
            )}
          </div>
        </>
      )}
      {hasConflictButHasDeclined && (
        <>
          <div className="cw-bg-neutral cw-flex cw-flex-col cw-caption cw-p-4 cw-pb-2 cw-rounded cw-gap-3">
            {!isExpanded && (
              <>
                <EventAttendeesAvatars org={org} event={event} />
                <AttendeesSlotPainsSummary org={org} persons={personList} event={event} />
              </>
            )}
            {isExpanded && <AttendeesSlotPains org={org} event={event} onInvite={onInvite} />}
            <ShowMoreShowLess isExpanded={isExpanded} handleClick={onShowMoreShowLessClick} />
          </div>
        </>
      )}
      {hasConflictButHasNotDeclined && (
        <>
          <div className="cw-bg-warning cw-flex cw-flex-col cw-caption cw-p-4 cw-pb-2 cw-rounded cw-gap-3">
            {!isExpanded && (
              <>
                <EventAttendeesAvatars org={org} event={event} />
                <AttendeesSlotPainsSummary org={org} persons={personList} event={event} />
              </>
            )}
            {isExpanded && <AttendeesSlotPains org={org} event={event} onInvite={onInvite} />}
            <ShowMoreShowLess isExpanded={isExpanded} handleClick={onShowMoreShowLessClick} />
          </div>
        </>
      )}
    </div>
  );
}
